import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Send, Globe, UsersRound, Trash2 } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '');

function AudiencePicker({ audience, setAudience, cohorts, selected, setSelected }) {
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const opt = (id, label, Icon) => (
    <button type="button" onClick={() => setAudience(id)} data-testid={`audience-${id}`}
      className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border text-sm transition-colors ${audience === id ? 'border-navigate-gold bg-navigate-gold/10 text-white' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );
  return (
    <div className="space-y-3">
      <div className="flex gap-3">{opt('all', 'All users on the platform', Globe)}{opt('cohorts', 'Selected cohorts', UsersRound)}</div>
      {audience === 'cohorts' && (
        cohorts.length === 0 ? <p className="text-xs text-amber-300">No cohorts yet — create them in the Cohorts section first.</p> : (
          <div className="flex flex-wrap gap-2" data-testid="audience-cohort-list">
            {cohorts.map((c) => (
              <button type="button" key={c.cohort_id} onClick={() => toggle(c.cohort_id)} data-testid={`audience-cohort-${c.cohort_id}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${selected.includes(c.cohort_id) ? 'bg-navigate-gold text-black border-navigate-gold' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                {c.name} <span className="opacity-60">({c.member_count})</span>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default function AnnouncementsAdmin({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [cohorts, setCohorts] = useState([]);
  const [sent, setSent] = useState(null);
  const [form, setForm] = useState({ title: '', message: '' });
  const [audience, setAudience] = useState('all');
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [alsoEmail, setAlsoEmail] = useState(false);

  const load = useCallback(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/announcements`, { headers: h }).then((r) => setSent(r.data.announcements || [])).catch(() => setSent([]));
    axios.get(`${API}/admin/cohorts`, { headers: h }).then((r) => setCohorts(r.data.cohorts || [])).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? It will disappear from every recipient's dashboard and notifications.`)) return;
    try {
      await axios.delete(`${API}/admin/announcements/${a.announcement_id}`, { headers });
      toast.success('Announcement deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete announcement');
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (audience === 'cohorts' && selected.length === 0) { toast.error('Pick at least one cohort'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/announcements`, { ...form, audience, cohort_ids: selected, send_email: alsoEmail }, { headers });
      toast.success(`Announcement sent to ${r.data.recipient_count} user(s)${r.data.emailed ? ' — email queued (see Admin → Email for delivery)' : ''}`);
      setAlsoEmail(false);
      setForm({ title: '', message: '' });
      setSelected([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send announcement'); }
    finally { setSending(false); }
  };

  return (
    <div className="p-8" data-testid="announcements-admin">
      <h1 className="font-heading text-3xl font-bold text-white mb-1">Announcements</h1>
      <p className="text-sm text-zinc-400 mb-8">Recipients see it on their dashboard and in the notification bell. Tick “Also email” to send it by email too.</p>
      <form onSubmit={send} className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 space-y-4 mb-10 max-w-3xl">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" maxLength={150}
          className="w-full h-10 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white px-3" data-testid="announcement-title-input" />
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message" rows={5}
          className="w-full bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-white p-3" data-testid="announcement-message-input" />
        <AudiencePicker audience={audience} setAudience={setAudience} cohorts={cohorts} selected={selected} setSelected={setSelected} />
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input type="checkbox" checked={alsoEmail} onChange={(e) => setAlsoEmail(e.target.checked)} className="accent-amber-400" data-testid="announcement-also-email" />
          Also email this to recipients <span className="text-xs text-zinc-500">(uses the settings in Admin → Email)</span>
        </label>
        <button type="submit" disabled={sending || !form.title.trim() || !form.message.trim() || (audience === 'cohorts' && !selected.length)} className="btn-gold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="send-announcement-btn">
          <Send className="h-4 w-4" />{sending ? 'Sending...' : 'Send announcement'}
        </button>
      </form>
      <h2 className="text-lg font-semibold text-white mb-3">Sent announcements</h2>
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50"><tr>
            {['Title', 'Date', 'Audience', 'Recipients', 'Emailed', ''].map((h) => <th key={h || 'actions'} className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {sent === null ? <tr><td colSpan={6} className="px-6 py-8 text-center text-zinc-500">Loading...</td></tr>
              : sent.length === 0 ? <tr><td colSpan={6} className="px-6 py-12 text-center text-zinc-500" data-testid="announcements-empty"><Megaphone className="h-8 w-8 mx-auto mb-2 text-zinc-600" />Nothing sent yet.</td></tr>
              : sent.map((a) => (
                <tr key={a.announcement_id} data-testid={`announcement-row-${a.announcement_id}`}>
                  <td className="px-6 py-4"><p className="text-sm text-white font-medium">{a.title}</p><p className="text-xs text-zinc-500 line-clamp-1">{a.message}</p></td>
                  <td className="px-6 py-4 text-sm text-zinc-300 whitespace-nowrap">{fmt(a.created_at)}</td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{a.audience === 'all' ? 'All users' : (a.cohort_names || []).join(', ')}</td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{a.recipient_count}</td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{a.emailed ? 'Yes' : 'No'}</td>
                  <td className="px-6 py-4 text-right">
                    <button type="button" onClick={() => remove(a)} title="Delete announcement" className="p-2 rounded-lg text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" data-testid={`announcement-delete-${a.announcement_id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
